import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaSpinner } from 'react-icons/fa';
import { jobCardService, userService } from '../../../../services/apiService';
import { toast } from 'react-toastify';

const NewJobCardDraft = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    customerId: '',
    vehicle: {
      registrationNumber: '',
      make: '',
      model: '',
      variant: '',
      year: '',
      vin: ''
    },
    dropoffAt: new Date().toISOString().slice(0, 16),
    bayId: '',
    reportedIssues: [''],
    notes: ''
  });

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const list = await userService.getAllUsers({ role: 'customer' });
        setCustomers(Array.isArray(list) ? list : []);
      } catch (e) {
        setCustomers([]);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  const handleVehicleChange = (field, value) => {
    setFormData((p) => ({
      ...p,
      vehicle: { ...p.vehicle, [field]: value }
    }));
  };

  const handleReportedIssueChange = (idx, value) => {
    setFormData((p) => {
      const issues = [...(p.reportedIssues || [''])];
      issues[idx] = value;
      return { ...p, reportedIssues: issues };
    });
  };

  const addReportedIssue = () => {
    setFormData((p) => ({
      ...p,
      reportedIssues: [...(p.reportedIssues || []), '']
    }));
  };

  const removeReportedIssue = (idx) => {
    setFormData((p) => ({
      ...p,
      reportedIssues: (p.reportedIssues || []).filter((_, i) => i !== idx)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId?.trim()) {
      toast.error('Please select a customer');
      return;
    }
    const issues = (formData.reportedIssues || []).filter((s) => (s || '').trim());
    if (issues.length === 0) {
      toast.error('Please add at least one reported issue');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        customerId: formData.customerId,
        vehicle: formData.vehicle,
        dropoffAt: formData.dropoffAt,
        bayId: formData.bayId || undefined,
        reportedIssues: issues,
        notes: formData.notes || undefined
      };
      const result = await jobCardService.create(payload);
      const id = result?._id || result?.id;
      toast.success('Job card draft created. Share link: ' + (result?.shareLink || 'N/A'));
      navigate(`/garage/dashboard/jobs/${id}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create job card');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4 border-b pb-4">
        <button
          onClick={() => navigate('/garage/dashboard/jobs')}
          className="text-gray-500 hover:text-black"
        >
          <FaArrowLeft size={20} />
        </button>
        <h2 className="text-2xl font-bold text-gray-900">New Job Card (Draft)</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="bg-white p-6 border rounded-lg shadow-sm">
          <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">1. Customer & Drop-off</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Customer *</label>
              <select
                required
                value={formData.customerId}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
              >
                <option value="">Select customer</option>
                {customers.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name || c.email || c.phone || c._id}
                  </option>
                ))}
              </select>
              {loadingCustomers && <p className="text-xs text-gray-500 mt-1">Loading customers…</p>}
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Drop-off Time</label>
              <input
                type="datetime-local"
                value={formData.dropoffAt}
                onChange={(e) => setFormData({ ...formData, dropoffAt: e.target.value })}
                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bay ID (optional)</label>
              <input
                type="text"
                value={formData.bayId}
                onChange={(e) => setFormData({ ...formData, bayId: e.target.value })}
                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                placeholder="e.g. Bay 1"
              />
            </div>
          </div>
        </section>

        <section className="bg-white p-6 border rounded-lg shadow-sm">
          <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">2. Vehicle</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Registration No</label>
              <input
                type="text"
                value={formData.vehicle.registrationNumber}
                onChange={(e) => handleVehicleChange('registrationNumber', e.target.value)}
                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                placeholder="e.g. MH01AB1234"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Make</label>
              <input
                type="text"
                value={formData.vehicle.make}
                onChange={(e) => handleVehicleChange('make', e.target.value)}
                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                placeholder="e.g. Maruti"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Model</label>
              <input
                type="text"
                value={formData.vehicle.model}
                onChange={(e) => handleVehicleChange('model', e.target.value)}
                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                placeholder="e.g. Swift"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Variant / Year</label>
              <input
                type="text"
                value={formData.vehicle.variant}
                onChange={(e) => handleVehicleChange('variant', e.target.value)}
                className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                placeholder="e.g. VXI 2020"
              />
            </div>
          </div>
        </section>

        <section className="bg-white p-6 border rounded-lg shadow-sm">
          <h3 className="text-lg font-bold mb-4 text-gray-800 border-b pb-2">3. Reported Issues</h3>
          <div className="space-y-2">
            {(formData.reportedIssues || ['']).map((issue, idx) => (
              <div key={idx} className="flex gap-2">
                <input
                  type="text"
                  value={issue}
                  onChange={(e) => handleReportedIssueChange(idx, e.target.value)}
                  className="flex-1 border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                  placeholder="Describe issue"
                />
                <button
                  type="button"
                  onClick={() => removeReportedIssue(idx)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addReportedIssue}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <FaPlus /> Add issue
            </button>
          </div>
        </section>

        <section className="bg-white p-6 border rounded-lg shadow-sm">
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            rows={3}
            placeholder="Additional notes"
          />
        </section>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <FaSpinner className="animate-spin" />}
            Create Draft
          </button>
          <button
            type="button"
            onClick={() => navigate('/garage/dashboard/jobs')}
            className="py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewJobCardDraft;
